import { useEffect, useState } from "react";
import { Button, Group, Select, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client";
import { type Show, shows } from "./api";

/**
 * Where a finished show's link sends people (`R-END-6a`).
 *
 * Offered once the show has ended or been cancelled, beside the link it redirects. The list is
 * every other show, newest first, so *tonight's show* is at the top; picking one saves at once,
 * because there is nothing else to fill in.
 *
 * A draft target is allowed and said to be waiting: the share page only follows a redirect onto a
 * show the public can open, so it starts working the moment that show is published — which is the
 * order a host sets things up in.
 */
export function ShowRedirect({
  show,
  mayEdit,
  onChanged,
}: {
  show: Show;
  /** The host's, on a finished show. */
  mayEdit: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [others, setOthers] = useState<Show[] | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    void shows
      .list()
      .then((answer) => {
        if (alive) setOthers(answer.shows.filter((other) => other.id !== show.id));
      })
      .catch(() => {
        if (alive) setError(t("shows.unreachable"));
      });
    return () => {
      alive = false;
    };
  }, [show.id, t]);

  async function save(target: string | null) {
    setBusy(true);
    setError(undefined);
    try {
      await shows.edit(show.id, { redirectShowId: target });
      await onChanged();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t("shows.unreachable"));
    } finally {
      setBusy(false);
    }
  }

  const target = others?.find((other) => other.id === show.redirectShowId);

  return (
    <Stack gap={6}>
      <Text size="xs" fw={600}>
        {t("shows.redirectLabel")}
      </Text>
      {mayEdit ? (
        <Group gap="xs" align="flex-end" wrap="wrap">
          <Select
            size="xs"
            w={320}
            placeholder={t("shows.redirectNone")}
            data={(others ?? []).map((other) => ({
              value: other.id,
              label: `${other.title} · ${t(`shows.state.${other.state}`)}`,
            }))}
            value={show.redirectShowId}
            onChange={(value) => {
              if (value && value !== show.redirectShowId) void save(value);
            }}
            searchable
            allowDeselect={false}
            disabled={busy || others === undefined}
          />
          {show.redirectShowId ? (
            <Button
              size="compact-xs"
              variant="subtle"
              color="red"
              disabled={busy}
              onClick={() => void save(null)}
            >
              {t("shows.redirectRemove")}
            </Button>
          ) : null}
        </Group>
      ) : (
        <Text size="xs" c="dimmed">
          {target ? target.title : t("shows.redirectNone")}
        </Text>
      )}
      <Text size="xs" c="dimmed">
        {target?.state === "draft"
          ? t("shows.redirectWaiting")
          : show.redirectShowId
            ? t("shows.redirectActive")
            : t("shows.redirectHelp")}
      </Text>
      {error ? (
        <Text size="xs" c="red">
          {error}
        </Text>
      ) : null}
    </Stack>
  );
}
